import {useRef, useState} from "react";
import {flushSync} from "react-dom";
import {ActionIcon, CloseButton} from "@mantine/core";
import {useTranslation} from "react-i18next";
import IconSearch from "../../../assets/search.svg?react";
import {SearchInput} from "./SearchInput";
import styles from "./checklist.module.css";

type CollapsingSearchProps = {
    searchTerm: string;
    onSearchChange: (term: string) => void;
};

/**
 * Search that collapses to an icon button when there isn't room for a full
 * input. Clicking the icon expands it to a full-width input (an overlay on the
 * nearest positioned ancestor) and focuses it. The icon keeps its place in the
 * row, invisible, while expanded, so the row's height and layout under the
 * overlay don't change. The "x" and Escape both clear the term, collapse and
 * focus the icon. Blur collapses only when the term is empty, so a collapsed
 * search never hides an active term.
 *
 * Focus moves only in response to the user's own expand/close actions, never on
 * mount: mounting with an active term shows the expanded input without
 * focusing it.
 *
 * `SearchInput` is reused untouched; this component owns only the
 * expand/collapse and "is empty" logic. Collapsing unmounts `SearchInput`
 * (cancelling any pending debounce), and expanding mounts a fresh one from
 * `searchTerm`.
 */
export const CollapsingSearch = ({
    searchTerm,
    onSearchChange,
}: CollapsingSearchProps) => {
    const {t} = useTranslation();
    const [expanded, setExpanded] = useState(searchTerm.length > 0);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const toggleRef = useRef<HTMLButtonElement>(null);
    // Mirror the latest debounced term so blur can decide whether to collapse.
    const valueRef = useRef(searchTerm);

    // flushSync commits the new render before focusing, so the target exists
    // (input) or is visible again (icon).
    const expand = () => {
        flushSync(() => setExpanded(true));
        wrapperRef.current?.querySelector("input")?.focus();
    };

    const collapse = () => {
        flushSync(() => setExpanded(false));
        toggleRef.current?.focus();
    };

    const handleChange = (term: string) => {
        valueRef.current = term;
        onSearchChange(term);
    };

    const handleClose = () => {
        valueRef.current = "";
        onSearchChange("");
        collapse();
    };

    return (
        <>
            {/* Stays in the row, invisible, while expanded: the overlay is out
                of flow, so without it a row with no filters loses its height. */}
            <ActionIcon
                ref={toggleRef}
                variant="default"
                size="lg"
                onClick={expand}
                aria-label={t("common.search")}
                title={t("common.search")}
                className={styles.searchToggle}
                data-expanded={expanded || undefined}
            >
                <IconSearch width={16} height={16} />
            </ActionIcon>

            {expanded && (
                <div
                    ref={wrapperRef}
                    className={styles.searchExpanded}
                    onBlur={(e) => {
                        // Collapse only when focus leaves the whole field, and only if empty.
                        if (
                            !e.currentTarget.contains(
                                e.relatedTarget as Node
                            ) &&
                            valueRef.current.length === 0
                        ) {
                            setExpanded(false);
                        }
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Escape") handleClose();
                    }}
                >
                    <div className={styles.searchField}>
                        <SearchInput
                            initialValue={searchTerm}
                            onChange={handleChange}
                        />
                    </div>
                    <CloseButton
                        aria-label={t("Action.CloseSearch", {
                            defaultValue: "Close search",
                        })}
                        // Keep focus in the input on press, so the blur handler can't
                        // collapse before the click (Safari doesn't focus buttons).
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleClose}
                    />
                </div>
            )}
        </>
    );
};
